/** Owns the explicit, browser-persisted TrackInsiders color theme. */

import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type ReactNode
} from "react";

export const THEME_STORAGE_KEY = "trackinsiders-theme";

export type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  selectTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isTheme(value: unknown): value is Theme {
  return value === "dark" || value === "light";
}

function storedTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function initialTheme(): Theme {
  if (typeof document !== "undefined" && isTheme(document.documentElement.dataset.theme)) {
    return document.documentElement.dataset.theme;
  }
  return storedTheme() ?? "dark";
}

function applyTheme(theme: Theme): void {
  if (typeof document !== "undefined") document.documentElement.dataset.theme = theme;
}

function persistTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage may be unavailable in privacy-restricted browsing contexts.
  }
}

/** Provides one theme preference to the landing, auth, and dashboard surfaces. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useLayoutEffect(() => applyTheme(theme), [theme]);

  function selectTheme(nextTheme: Theme) {
    applyTheme(nextTheme);
    persistTheme(nextTheme);
    setTheme(nextTheme);
  }

  return <ThemeContext.Provider value={{ theme, selectTheme }}>{children}</ThemeContext.Provider>;
}

/** Reads and updates the current explicit TrackInsiders theme. */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error("useTheme must be used within ThemeProvider");
  return value;
}
